import { useState } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import useLocalization from '../../../../hooks/useLocalization';
import {
  STANDINGS_LEGEND_LABEL_KEYS,
  STANDINGS_VIEW_TAB_LABEL_KEYS,
  STANDINGS_VIEW_TABS,
  STANDINGS_VIEW_TITLE_KEYS,
} from '../constants';
import { getStandingZoneColor } from '../helpers';
import { styles, WIDGET_COLORS } from '../styles';
import type { PreMatchWidgetData, PreMatchWidgetStandingsView } from '../types';

import { mergeSx } from '../../../../utils/styles';

type StandingsPanelProps = {
  data: PreMatchWidgetData;
};

const StandingsPanel = ({ data }: StandingsPanelProps) => {
  const { t } = useLocalization();
  const [activeView, setActiveView] = useState<PreMatchWidgetStandingsView>('league');
  const standings = {
    league: [],
    home: [],
    away: [],
    legend: [],
    ...(data.standings || {}),
  };
  const currentStandings = standings[activeView];

  return (
    <Box>
      <Box sx={styles.standingsViewTabs} role='tablist' aria-label={t('preMatchWidget.standings.viewTablist')}>
        {STANDINGS_VIEW_TABS.map((item) => {
          const isActive = item.value === activeView;

          return (
            <Box
              key={item.value}
              component='button'
              type='button'
              role='tab'
              aria-selected={isActive}
              onClick={() => setActiveView(item.value)}
              sx={mergeSx(styles.standingsViewTab, isActive && styles.standingsViewTabActive)}
            >
              {t(STANDINGS_VIEW_TAB_LABEL_KEYS[item.value], {
                defaultValue: item.label,
              })}
            </Box>
          );
        })}
      </Box>
      <Typography sx={styles.standingsSectionTitle}>{t(STANDINGS_VIEW_TITLE_KEYS[activeView])}</Typography>
      {currentStandings.length ? (
        <Box sx={styles.standingsTableWrapper} data-testid='standings-table-wrapper'>
          <Table size='small' sx={styles.standingsTable}>
            <colgroup>
              <col style={{ width: '32px' }} />
              <col />
              <col style={{ width: '40px' }} />
              <col style={{ width: '40px' }} />
              <col style={{ width: '44px' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.standingsPositionCell}>#</TableCell>
                <TableCell>{t('preMatchWidget.standings.columns.team')}</TableCell>
                <TableCell align='right'>{t('preMatchWidget.standings.columns.matchesPlayed')}</TableCell>
                <TableCell align='right'>{t('preMatchWidget.standings.columns.goalDifference')}</TableCell>
                <TableCell align='right'>{t('preMatchWidget.standings.columns.points')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentStandings.map((entry) => (
                <TableRow
                  key={`${activeView}-${entry.teamName}`}
                  sx={entry.highlighted ? styles.highlightedTableRow : undefined}
                >
                  <TableCell sx={styles.standingsPositionCell}>{entry.position}</TableCell>
                  <TableCell>
                    <Box sx={styles.standingsTeamCell}>
                      <Box
                        sx={{
                          ...styles.standingsZoneMarker,
                          backgroundColor: getStandingZoneColor(entry.zone ?? 'neutral'),
                        }}
                      />
                      <Typography sx={styles.standingsTeamLabel} title={entry.teamName}>
                        {entry.teamName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align='right'>{entry.played}</TableCell>
                  <TableCell align='right'>{entry.goalDifference}</TableCell>
                  <TableCell align='right' sx={{ color: WIDGET_COLORS.accent, fontWeight: 800 }}>
                    {entry.points}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Typography sx={styles.summaryDescription}>{t('preMatchWidget.standings.empty')}</Typography>
      )}
      {currentStandings.length && standings.legend.length ? (
        <Box sx={styles.standingsLegend}>
          {standings.legend.map((item) => (
            <Box key={`${activeView}-${item.label}`} sx={styles.standingsLegendRow}>
              <Box sx={{ ...styles.standingsLegendSwatch, backgroundColor: getStandingZoneColor(item.zone) }} />
              <Typography sx={styles.standingsLegendLabel}>
                {t(STANDINGS_LEGEND_LABEL_KEYS[item.zone], {
                  defaultValue: item.label,
                })}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default StandingsPanel;
